import { ClearOutlined, DownOutlined } from '@ant-design/icons';
import { DefaultOptionType } from '@bugly/common';
import { CharacterInput, CharacterInputValue, CustomInputRenderProps } from '@tencent/tds-ui';
import { useControllableValue } from 'ahooks';
import { Button, Dropdown, Form, InputNumber, InputNumberProps, SelectProps, Space, Tooltip } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import SwapRight from '@/assets/images/icons/SwapRight.react.svg?react';
import { MatchMode, NUMBER_MATCH_OPTIONS } from '@/presentation/components/form/standards/types';
import { NUMBER_MAX_VALUE } from '@/presentation/components/tds-form/config';
import { TdsInput, TdsInputNumber } from '@/presentation/components/tds-form/tds-input';
import { TdsInputNumberRange } from '@/presentation/components/tds-form/tds-number-range';
import { BaseFormItemProps, PropsWithStyles } from '@/presentation/components/tds-form/type';
import styles from './index.module.scss';

export interface TdsNumberInputProps<T = any> extends BaseFormItemProps<T> {
  modeSelectProps?: SelectProps;
  valueSelectProps?: TdsNumberInputProps | InputNumberProps;
  modeOptions?: DefaultOptionType[];
  defaultActiveFirstMode?: boolean;
  dropDownMode?: boolean;
}

// todo 应该改个名字
export const TdsNumberInput = (props: PropsWithStyles<TdsNumberInputProps>) => {
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const [value, setValue] = useControllableValue<CharacterInputValue>(props, {
    defaultValue: {
      mode: undefined,
      values: undefined,
    },
  });

  const ref = useRef<any>();
  const [filterText, setFilterText] = useState('');

  const {
    valueSelectProps,
    modeSelectProps,
    modeOptions = NUMBER_MATCH_OPTIONS,
    defaultActiveFirstMode = true,
    dropDownMode = false,
  } = props;

  const showOverlay = () => setVisible(true);
  const hideOverlay = () => setVisible(false);

  const onConfirm = () => {
    const values = form.getFieldsValue();
    hideOverlay();
    if (!values?.min && !values?.max) {
      setFilterText('');
    } else {
      setFilterText(`${values?.min ?? ''}~${values?.max ?? ''}`);
    }

    setValue({
      ...value,
      values: {
        min: values?.min,
        max: values?.max,
      },
    });
  };

  const overlay = (
    <div className={styles.customOverlay}>
      <div>
        <Form form={form} autoFocus>
          <Form.Item name='min' label='' noStyle>
            <InputNumber
              max={NUMBER_MAX_VALUE}
              controls={false}
            />
          </Form.Item>
          <>
            <SwapRight />
            <Form.Item name='max' label='' noStyle>
              <InputNumber
                max={NUMBER_MAX_VALUE}
                controls={false}
              />
            </Form.Item>
          </>
          <Tooltip title='重置'>
            <ClearOutlined style={{ marginLeft: 12 }} onClick={() => form.resetFields()} />
          </Tooltip>
        </Form>
      </div>
      <div className={styles.footer}>
        <Space size={10}>
          <Button onClick={hideOverlay} size='small'>取消</Button>
          <Button type={'primary'} onClick={onConfirm} size='small'>确定</Button>
        </Space>
      </div>
    </div>
  );

  const dropDownRender = () => (
    <div className={styles.selectRightLayer} style={{ position: 'relative', width: '100%' }}>
      <TdsInput
        placeholder='选择范围'
        onClick={showOverlay}
        suffix={<DownOutlined style={{ color: 'rgba(0, 0, 0, 0.25)', fontSize: '12px' }}/>}
        style={{ width: '100%' }}
        value={filterText}
      />
      <Dropdown
        overlay={overlay}
        trigger={['click']}
        visible={visible}
        destroyPopupOnHide={false}
        onVisibleChange={visible => setVisible(visible)}
      >
        <div style={{ height: 1 }}/>
      </Dropdown>
    </div>
  );

  const customInputRender = (params: CustomInputRenderProps) => (value.mode === MatchMode.Between ? <TdsInputNumberRange
    {...valueSelectProps}
    value={params.value}
    onChange={params.onChange}
  /> : <TdsInputNumber
    {...valueSelectProps}
    onChange={params.onChange}
    controls={false}
    className={'flex-1'}
    placeholder={'请输入'}
    value={params.value}
  />);

  useEffect(() => {
    if (!value?.values?.min && !value?.values?.max) {
      setFilterText('');
    } else {
      setFilterText(`${value?.values?.min ?? ''}~${value?.values?.max ?? ''}`);
    }
  }, [value]);

  return (
    <CharacterInput
      className={props.className}
      style={props.style}
      ref={ref}
      value={value}
      defaultActiveFirstMode={defaultActiveFirstMode}
      modeSelectProps={{
        dropdownMatchSelectWidth: 100,
        options: modeOptions,
        ...modeSelectProps,
      }}
      customInputRender={dropDownMode ? dropDownRender : customInputRender}
      onChange={(value) => {
        setValue(value);
      }}
    />
  );
};
